import React from 'react'

import {
  Beta,
  IconChevronLeftDefault,
  IconChevronRightDefault,
} from '@mc/design-system'
import { TrackingContainer } from '@mc/visibility'
import { useTranslation } from '@mc/i18n'
import { Course, Chapter, CourseSubType } from 'types/data'
import { chapterPath, coursePath, sessionsClassesPath } from 'libs/paths'
import { ChapterSubType } from 'libs/constants'

import { RelatedContentTile } from './RelatedContentTile'
import { trackRelatedContentCarouselViewed } from './events'

interface RelatedContentCarouselProps {
  content: (Chapter | Course)[]
  scrollCount: number
  showCount: number
  aspectRatio: '16x9' | '1x1'
}

export const RelatedContentCarousel = ({
  content,
  scrollCount,
  aspectRatio,
  showCount,
}: RelatedContentCarouselProps) => {
  const { t } = useTranslation('@mc/next')

  return (
    <TrackingContainer onFirstVisible={trackRelatedContentCarouselViewed}>
      <Beta.Carousel
        scrollCount={scrollCount}
        srLabel={t('episode.relatedContent.carouselSrLabel')}
      >
        <Beta.CarouselArrows>
          <Beta.CarouselPrevious kind='secondary' symmetrical rounded>
            <IconChevronLeftDefault />
          </Beta.CarouselPrevious>
          <Beta.CarouselContent>
            {content.map((item, idx) => (
              <div
                className={`col-${12 / Number(showCount)} mc-px-0`}
                key={idx}
              >
                <Beta.CarouselItem>
                  {[ChapterSubType.CHAPTER, ChapterSubType.EPISODE].includes(
                    item.type || '',
                  ) ? (
                    <EpisodeTile
                      episode={item as Chapter}
                      aspectRatio={aspectRatio}
                    />
                  ) : (
                    <CourseTile
                      course={item as Course}
                      aspectRatio={aspectRatio}
                    />
                  )}
                </Beta.CarouselItem>
              </div>
            ))}
          </Beta.CarouselContent>
          <Beta.CarouselNext kind='secondary' symmetrical rounded>
            <IconChevronRightDefault />
          </Beta.CarouselNext>
        </Beta.CarouselArrows>
      </Beta.Carousel>
    </TrackingContainer>
  )
}

interface EpisodeTileProps {
  episode: Chapter
  aspectRatio: '16x9' | '1x1'
}

const EpisodeTile = ({ episode, aspectRatio }: EpisodeTileProps) => {
  const course = episode.course as Course
  return (
    <RelatedContentTile
      title={episode.title}
      subtitle={course?.title || ''}
      imageUrl={episode.videoThumbUrl || ''}
      aspectRatio={aspectRatio}
      href={chapterPath(course.slug, episode.slug)}
      slug={episode.slug || ''}
    />
  )
}

interface CourseTileProps {
  course: Course
  aspectRatio: '16x9' | '1x1'
}

const CourseTile = ({ course, aspectRatio }: CourseTileProps) => {
  const getHref = () => {
    if (course.type === CourseSubType.CAMP) {
      return sessionsClassesPath(course.slug)
    }

    return coursePath(course.slug)
  }

  return (
    <RelatedContentTile
      title={course.displayTitle || ''}
      subtitle={course.title || ''}
      imageUrl={
        (aspectRatio === '1x1' ? course.primary1x1 : course.primary16x9) || ''
      }
      aspectRatio={aspectRatio}
      href={getHref()}
      slug={course.slug || ''}
    />
  )
}
